import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { formatMoney, toNumber } from '@shop/shared'
import { Banner, Empty, ErrorBanner, Spinner } from '@shop/ui'
import { Keypad, TouchButton } from '../pos/touch'
import * as pos from '../pos/api'
import type { CashPayout, CashSessionReport, PayoutCategory } from '../pos/contracts'

/**
 * أبوابُ المصروف من الدرج — <b>من دفتر المالك في الإكسل</b>: يوميّةُ مجد، دفعةُ
 * أبي آدم، توصيلٌ وأكياس، إنترنت، قسطُ الجمعيّة.
 */
const CATEGORIES: readonly { id: PayoutCategory; label: string; hint: string }[] = [
  { id: 'EMPLOYEE', label: 'موظّف', hint: 'يوميّة · راتب · سلفة · عمولة' },
  { id: 'SUPPLIER', label: 'مورّد', hint: 'دفعةٌ تُنقص ما له علينا' },
  { id: 'SHOP', label: 'مصاريف المحلّ', hint: 'قرطاسيّة · أكياس · توصيل' },
  { id: 'INTERNET', label: 'إنترنت وهاتف', hint: '' },
  { id: 'JAMIYA', label: 'الجمعيّة', hint: 'القسط الشهريّ' },
  { id: 'OTHER', label: 'أخرى', hint: 'اكتب ما هو' },
]

const LABELS: Record<PayoutCategory, string> = Object.fromEntries(
  CATEGORIES.map((c) => [c.id, c.label]),
) as Record<PayoutCategory, string>

/**
 * المصروفُ من الدرج — <b>طلبُ المالك ٢٠٢٦-١٠-٠٧</b>.
 *
 * ⚠ <b>وكان الدرجُ يكذب بالضرورة:</b> يوميّةُ مجدٍ ودفعاتُ المورّدين تخرج منه كلَّ
 *   يوم والنظامُ لا يعرف — فالمتوقَّعُ أكبرُ من الدرج دائمًا. وهنا كلُّ دينارٍ
 *   يخرج يُكتب في الورديّة، ويُطرح من المتوقَّع، ويُرحَّل إلى بابه في الدفتر.
 *
 * ⚠ <b>ولا يخرج أكثرُ ممّا في الدرج</b> — والخادمُ يرفض، والشاشةُ تقول كم فيه.
 */
export default function Payouts({
  sessionId,
  report,
}: {
  sessionId: string
  report: CashSessionReport | null
}) {
  const queries = useQueryClient()

  const [open, setOpen] = useState(false)
  const [category, setCategory] = useState<PayoutCategory>('EMPLOYEE')
  const [partyId, setPartyId] = useState('')
  const [amount, setAmount] = useState('')
  const [memo, setMemo] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [voiding, setVoiding] = useState<string | null>(null)
  const [reason, setReason] = useState('')

  const options = useQuery({
    queryKey: ['payout-options'],
    queryFn: ({ signal }) => pos.payoutOptions(signal),
    enabled: open,
    staleTime: 60_000,
  })

  const payees =
    category === 'EMPLOYEE'
      ? options.data?.employees ?? []
      : category === 'SUPPLIER'
        ? options.data?.suppliers ?? []
        : []

  const needsParty = category === 'EMPLOYEE' || category === 'SUPPLIER'
  const value = toNumber(amount) ?? 0
  const inDrawer = report?.expectedCashNow ?? null
  const lines: CashPayout[] = report?.payoutLines ?? []

  const ready =
    value > 0
    && (!needsParty || partyId !== '')
    && (category !== 'OTHER' || memo.trim().length > 0)
    && (inDrawer === null || value <= inDrawer)

  const refresh = () =>
    Promise.all([
      queries.invalidateQueries({ queryKey: ['shift-report', sessionId] }),
      queries.invalidateQueries({ queryKey: ['board'] }),
    ])

  const save = async () => {
    setBusy(true)
    setError(null)

    try {
      await pos.createPayout(sessionId, {
        category,
        amount: value,
        partyId: needsParty ? partyId : null,
        memo: memo.trim() || null,
      })

      setAmount('')
      setMemo('')
      setPartyId('')
      setOpen(false)
      await refresh()
    } catch (failure) {
      setError(failure)
    } finally {
      setBusy(false)
    }
  }

  const cancel = async (id: string) => {
    setBusy(true)
    setError(null)

    try {
      await pos.voidPayout(id, reason.trim())
      setVoiding(null)
      setReason('')
      await refresh()
    } catch (failure) {
      setError(failure)
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="grid gap-2">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold">مصاريفُ من الدرج</h3>
        {report && (
          <span className="text-xs text-ink-soft">
            في الدرج الآن <b className="tabular text-ink" dir="ltr">{formatMoney(report.expectedCashNow)}</b>
          </span>
        )}
      </div>

      <ErrorBanner error={error ?? options.error} />

      {lines.length === 0 ? (
        !open && <Empty>لم يخرج شيءٌ من الدرج في هذه الورديّة.</Empty>
      ) : (
        <ul className="grid gap-1">
          {lines.map((row) => (
            <li
              key={row.id}
              className={`rounded-lg border border-line px-3 py-2 text-sm ${row.voided ? 'opacity-50' : ''}`}
            >
              <div className="flex items-baseline gap-2">
                <span className={`min-w-0 flex-1 truncate ${row.voided ? 'line-through' : ''}`}>
                  {LABELS[row.category]}
                  {row.partyName && <> — <b>{row.partyName}</b></>}
                  {row.memo && <span className="text-ink-soft"> · {row.memo}</span>}
                </span>
                <span className="tabular shrink-0 font-bold text-warn" dir="ltr">
                  −{formatMoney(row.amount)}
                </span>
              </div>

              {row.voided ? (
                <p className="mt-1 text-xs text-ink-soft">أُلغي: {row.voidReason}</p>
              ) : voiding === row.id ? (
                <div className="mt-2 grid gap-2">
                  <input
                    className="min-h-12 rounded-lg border border-line bg-surface px-3"
                    placeholder="سببُ الإلغاء"
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <TouchButton
                      tone="danger"
                      disabled={busy || reason.trim().length === 0}
                      onClick={() => void cancel(row.id)}
                    >
                      ألغِ المصروف
                    </TouchButton>
                    <TouchButton onClick={() => setVoiding(null)}>تراجع</TouchButton>
                  </div>
                </div>
              ) : (
                <button
                  className="mt-1 text-xs text-ink-soft underline"
                  type="button"
                  onClick={() => { setVoiding(row.id); setReason('') }}
                >
                  كُتب خطأ؟ ألغِه
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {!open ? (
        <TouchButton wide onClick={() => setOpen(true)}>
          − مصروفٌ من الدرج
        </TouchButton>
      ) : (
        <div className="grid gap-3 rounded-xl border border-line p-3">
          <div className="grid grid-cols-3 gap-2">
            {CATEGORIES.map((c) => (
              <button
                key={c.id}
                type="button"
                className={`min-h-12 rounded-lg border px-2 text-sm font-semibold ${
                  category === c.id ? 'border-brand bg-brand/10 text-brand' : 'border-line bg-surface'
                }`}
                onClick={() => { setCategory(c.id); setPartyId('') }}
              >
                {c.label}
              </button>
            ))}
          </div>

          {CATEGORIES.find((c) => c.id === category)?.hint && (
            <p className="text-xs text-ink-soft">{CATEGORIES.find((c) => c.id === category)?.hint}</p>
          )}

          {needsParty && (
            options.isPending ? (
              <Spinner label="تُقرأ الأسماء…" />
            ) : payees.length === 0 ? (
              <Banner tone="warn">
                {category === 'EMPLOYEE' ? 'لا موظّفين مسجّلين بعد.' : 'لا مورّدين مسجّلين بعد.'}
              </Banner>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {payees.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className={`min-h-12 rounded-lg border px-2 text-sm ${
                      partyId === p.id ? 'border-brand bg-brand/10 font-semibold text-brand' : 'border-line bg-surface'
                    }`}
                    onClick={() => setPartyId(p.id)}
                  >
                    {p.name}
                  </button>
                ))}
              </div>
            )
          )}

          <div className="tabular rounded-lg border border-line bg-surface px-3 py-2 text-left text-2xl font-bold" dir="ltr">
            {amount || '0'}
          </div>
          <Keypad value={amount} onChange={setAmount} />

          {inDrawer !== null && value > inDrawer && (
            <Banner tone="warn">في الدرج {formatMoney(inDrawer)} فقط.</Banner>
          )}

          <input
            className="min-h-12 rounded-lg border border-line bg-surface px-3"
            placeholder={category === 'OTHER' ? 'ما هو؟ (إلزاميّ)' : 'ملاحظة (اختياريّ)'}
            value={memo}
            onChange={(event) => setMemo(event.target.value)}
          />

          <div className="grid grid-cols-2 gap-2">
            <TouchButton tone="primary" disabled={busy || !ready} onClick={() => void save()}>
              {busy ? 'يُحفظ…' : `أخرج ${formatMoney(value)}`}
            </TouchButton>
            <TouchButton onClick={() => setOpen(false)}>إلغاء</TouchButton>
          </div>
        </div>
      )}
    </section>
  )
}
